import { z } from "zod";

// ============================================================================
// WORKSPACE SCHEMAS (camelCase for API)
// ============================================================================

// Workspace roles
export const WorkspaceRoleSchema = z.enum(["super-admin", "admin", "owner", "user", "viewer"]);

// Base workspace schema (matches backend DbWorkspaceOutput)
const BaseWorkspaceSchema = z.object({
  id: z.string(),
  creatorId: z.string(),
  historyVersion: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  logoPublicUrl: z.string().nullable(),
  status: z.string(),
  archivedAt: z.string().nullable(),
  isVirtual: z.boolean().default(false),
});

export const WorkspaceSchema = BaseWorkspaceSchema;

export const WorkspaceListItemSchema = BaseWorkspaceSchema.extend({
  creatorFirstName: z.string().nullable().optional(),
  creatorLastName: z.string().nullable().optional(),
  creatorEmail: z.string().nullable().optional(),
  patientsCount: z.number().nullable().optional(),
  agentsCount: z.number().nullable().optional(),
  followUpsCount: z.number().nullable().optional(),
  membersCount: z.number().nullable().optional(),
});

export const WorkspaceListSchema = z.array(WorkspaceListItemSchema);

// Workspace member
export const WorkspaceMemberSchema = z.object({
  workspaceId: z.string(),
  userId: z.string(),
  role: WorkspaceRoleSchema,
  createdAt: z.string(),
  email: z.string().email().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
});

// ============================================================================
// CREATE/UPDATE PAYLOAD SCHEMAS
// ============================================================================

export const CreateWorkspaceBodySchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  logoPublicUrl: z.string().optional(),
  isVirtual: z.boolean().optional(),
});

export const UpdateWorkspaceBodySchema = z.object({
  name: z.string().min(1).optional(),
  description: z.string().optional(),
  logoPublicUrl: z.string().optional(),
  status: z.enum(["active", "inactive", "archived"]).optional(),
});

export const AddWorkspaceMemberBodySchema = z.object({
  userId: z.string().min(1, "User ID is required"),
  role: WorkspaceRoleSchema,
});

export const AddWorkspaceMemberByEmailBodySchema = z.object({
  email: z.string().email("Valid email is required"),
  role: WorkspaceRoleSchema,
});

export const UpdateWorkspaceMemberRoleBodySchema = z.object({
  userId: z.string().min(1, "User ID is required"),
  role: WorkspaceRoleSchema,
});

// ============================================================================
// TYPE EXPORTS
// ============================================================================

export type WorkspaceRole = z.infer<typeof WorkspaceRoleSchema>;
export type Workspace = z.infer<typeof WorkspaceSchema>;
export type WorkspaceListItem = z.infer<typeof WorkspaceListItemSchema>;
export type WorkspaceMember = z.infer<typeof WorkspaceMemberSchema>;
export type CreateWorkspaceBody = z.infer<typeof CreateWorkspaceBodySchema>;
export type UpdateWorkspaceBody = z.infer<typeof UpdateWorkspaceBodySchema>;
export type AddWorkspaceMemberBody = z.infer<typeof AddWorkspaceMemberBodySchema>;
export type UpdateWorkspaceMemberRoleBody = z.infer<typeof UpdateWorkspaceMemberRoleBodySchema>;

// API Response types
export type CreateWorkspaceApiResponse = Workspace;
export type UpdateWorkspaceApiResponse = Workspace;
export type WorkspaceListApiResponse = z.infer<typeof WorkspaceListSchema>;
